import { WIZ_EVENT_TYPE, WizDateUtils } from '@wizeoo/akm-core-lib';
import {
  WizDateField,
  WizTextInput,
  WizValidatorFn,
  WizValidationErrors,
  WizValidationContainerDirective,
  WizValidationError,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { ChronocodeVO } from '@wizeoo/akm-common-lib';
import { ALK_EVENT_TYPE } from '@wizeoo/akm-common-lib';
import { AlkEvent } from '@wizeoo/akm-common-lib';
import { ChronoLevelCreationGroup } from '../../creation/chrono-level-creation.group';
import { Model } from 'src/app/model/model';

/**
 * Composant d'édition du détail des chronocodes
 */
@Component({
  selector: 'chrono-detail-edit-renderer',
  templateUrl: './chrono-detail-edit.renderer.html',
})
export class ChronoDetailEditRenderer implements AfterViewInit, OnDestroy {
  // TODO gestion delete

  /** Datefield des saisie de date de fin */
  @ViewChild('hgMain', { read: ElementRef, static: false }) hgMain: ElementRef;
  /** Datefield des saisie de date de fin */
  @ViewChild('dfEndDate', { read: WizDateField, static: false }) dfEndDate: WizDateField;
  /** Datefield des saisie de date de debut */
  @ViewChild('dfStartDate', { read: WizDateField, static: false }) dfStartDate: WizDateField;
  /** Input de saisie de shortCode */
  @ViewChild('tiShortCode', { read: WizTextInput, static: false }) tiShortCode: WizTextInput;
  /** Zone de validation des champs de saisie */
  @ViewChild('hgMain', { read: WizValidationContainerDirective, static: false })
  private validationContainer: WizValidationContainerDirective;

  constructor(public elementRef: ElementRef) {}

  @Input() public chronocode: ChronocodeVO; /** Le chronocode manipulé */
  @Input() parentGroup: ChronoLevelCreationGroup; // le parent, pour synchro sur delete / update
  // @Input() public chronocodeList: ChronocodeVO[]; /** La liste de chronocodes frères */

  /** n'est pas le premier de son parent */
  public isFirst(): boolean {
    let result: boolean = false;
    const parent = this.getParent();
    if (this.chronocode.startDate === parent.startDate) {
      result = true;
    }
    return result;
  }

  /** n'est pas le dernier de son parent */
  public isLast(): boolean {
    let result: boolean = false;
    const parent = this.getParent();
    if (this.chronocode.endDate === parent.endDate) {
      result = true;
    }
    return result;
  }

  /** shortCode du chrono parent */
  public get parentLabel(): string {
    let result: string = '';
    if (this.getParent()) {
      result = this.getParent().shortCode;
    }
    return result;
  }

  /** accesseur sur le chorno parent de la simulation */
  private _parent: ChronocodeVO = null;
  public getParent(): ChronocodeVO {
    if (!this._parent) {
      this._parent = this.chronocode.simulatedParent;
    }
    return this._parent;
  }

  public ngAfterViewInit(): void {
    WizEventManager.addListener(this, this.hgMain, WIZ_EVENT_TYPE.MODEL_UPDATE, this.onChange);
    WizEventManager.addListener(
      this,
      this.parentGroup.elementRef,
      ALK_EVENT_TYPE.UPDATE_CHRONO_TEMPLATE_ITEM,
      this.update
    );
    setTimeout(() => {
      this.feed();
      this.manageComponents();
    }, 0);
  }

  public ngOnDestroy(): void {
    WizEventManager.removeListener(this, this.hgMain, WIZ_EVENT_TYPE.MODEL_UPDATE);
    WizEventManager.removeListener(this, this.parentGroup.elementRef, ALK_EVENT_TYPE.UPDATE_CHRONO_TEMPLATE_ITEM);
  }

  /** Rafraichissement du composant (appel externe de la vue) */
  public refresh(): void {
    this.feed();
    this.manageComponents();
  }

  /** retourne le précédent */
  public get previousChronocode(): ChronocodeVO {
    return this.chronocode.template.getPreviousChrono(this.chronocode);
  }

  /** retourne le précédent */
  public get nextChronocode(): ChronocodeVO {
    return this.chronocode.template.getNextChrono(this.chronocode);
  }

  /**
   * Alimentation des données
   */
  private feed(): void {
    this.dfStartDate.wizNumberValue = this.chronocode.startDate;
    this.dfEndDate.wizNumberValue = this.chronocode.endDate;
    this.tiShortCode.wizValue = this.chronocode.shortCode;
  }

  /**
   * Gestion des composants
   */
  private manageComponents(): void {
    this.dfStartDate.wizDisabled = this.isFirst();
    this.dfEndDate.wizDisabled = this.isLast();

    let minStartDate: Date;
    if (this.previousChronocode) {
      // on prend sa date de début + 2 (comme ça on peut écraser tranquille)
      minStartDate = WizDateUtils.add(WizDateUtils.numberToDate(this.previousChronocode.startDate), {
        days: 2,
      }) as Date;
    } else {
      minStartDate = WizDateUtils.numberToDate(this.chronocode.startDate);
    }
    this.dfStartDate.wizMinDate = minStartDate;
    this.dfStartDate.wizMaxDate = WizDateUtils.add(WizDateUtils.numberToDate(this.chronocode.endDate), {
      days: -1,
    }) as Date;

    let maxEndDate: Date;
    if (this.nextChronocode) {
      // on prends sa date de fin - 2
      maxEndDate = WizDateUtils.add(WizDateUtils.numberToDate(this.nextChronocode.endDate), { days: -2 }) as Date;
    } else {
      maxEndDate = WizDateUtils.numberToDate(this.chronocode.endDate);
    }
    this.dfEndDate.wizMinDate = WizDateUtils.add(WizDateUtils.numberToDate(this.chronocode.startDate), {
      days: 1,
    }) as Date;
    this.dfEndDate.wizMaxDate = maxEndDate;
  }

  /**
   * Ecoute de changement
   * @param e Evenement model update
   */
  private onChange(e: WizEvent): void {
    switch (e.component) {
      case this.dfStartDate:
        this.chronocode.startDate = this.dfStartDate.wizNumberValue;
        WizEventManager.dispatchEvent(
          this.hgMain,
          new AlkEvent(ALK_EVENT_TYPE.CHRONO_DATE_CHANGE, [this.chronocode, true])
        );
        this.manageComponents();
        break;
      case this.dfEndDate:
        this.chronocode.endDate = this.dfEndDate.wizNumberValue;
        WizEventManager.dispatchEvent(
          this.hgMain,
          new AlkEvent(ALK_EVENT_TYPE.CHRONO_DATE_CHANGE, [this.chronocode, false])
        );
        this.manageComponents();
        break;
      case this.tiShortCode:
        this.chronocode.shortCode = this.tiShortCode.wizValue;
        Model.getInstance().localeEngine.newTranslatableVO(this.chronocode, 'label'); // ajout sur le libellé
        // this.chronocode.trsLabel = this.tiShortCode.wizValue;
        break;
    }
  }

  /**
   * Validation des données
   * @param e : Evenement clic
   */
  public validationCheck(): WizValidationErrors {
    // Vérification de la saisie
    return this.validationContainer.check(true, true);
  }

  /**
   * Validateur d'unicité du shortcode
   * @return WizValidatorFn
   */
  public uniqueShortCodeValidator(): WizValidatorFn {
    return (): WizValidationErrors | null => {
      if (this.tiShortCode.wizValue !== null && this.tiShortCode.wizValue && this.tiShortCode.wizValue.length > 0) {
        const err: WizValidationError = this.chronocode.template.checkCodeUnicity(this.chronocode);
        if (!!err) {
          return [err];
        }
      }
      return null;
    };
  }

  /** suppression de l'entrée, son précédent prend le gap */
  public doDelete(): void {
    // dispatch pour gestion par le parent chronoLevelCreationGroup
    WizEventManager.dispatchEvent(
      this.elementRef,
      new AlkEvent(ALK_EVENT_TYPE.DELETE_CHRONO_TEMPLATE_ITEM, this.chronocode)
    );
  }

  /** modif externe sur delete, mise à jour si c'est moi */
  public update(e: AlkEvent): void {
    // if (e.data === this.chronocode) {
    this.feed(); // refresh des champs
    this.manageComponents(); // et gestion des autorisations
    // }
  }

  // TODO contrôle unicité de code

  // TODO build chrono depuis chronos customs
}
